import React, { useState } from "react";
import {
  Box,
  Button,
  Hidden,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from "@mui/material";
import { Version } from "./apps-types";
import { Delete } from "@mui/icons-material";
import DeleteElementFromTable from "../reusable/DeleteElementFromTableDialog";
import NoDataToBeShown from "../reusable/NoDataToBeShown";
import { useTranslation } from "react-i18next";

import LinkGenerationDesktop from "./LinkGeneration";

function ApplicationsDetailsTable(props: {
  name: string;
  platform: string;
  columns: string[];
  versions: Version[];
  appID: number;
  isLoading: boolean;
  allVersions?: Version[];
}) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const skeletonArray = Array(5).fill("");

  const { t } = useTranslation();
  const title = [t("Delete")];

  const [version, setVersion] = React.useState<string>("");

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const [openDelete, setOpenDelete] = useState(false);
  const [deletedVersion, setDeletedVersion] = useState<Version | undefined>(
    undefined
  );

  const handleSetDeleteInfo = (element: Version) => {
    setOpenDelete(true);
    setDeletedVersion(element);
  };

  return (
    <Box>
      <Box sx={{ width: "100%" }}>
        <TableContainer component={Paper} sx={{ width: "100%" }}>
          <Table sx={{ width: "100%" }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell key={props.columns[0]} align="center">
                  {props.columns[0]}
                </TableCell>
                <Hidden mdDown>
                  <TableCell key={props.columns[1]} align="center">
                    {props.columns[1]}
                  </TableCell>
                  <TableCell key={props.columns[2]} align="center">
                    {props.columns[2]}
                  </TableCell>
                </Hidden>
                <TableCell key="app-share-version" align="center" />
                {localStorage.getItem("Role") !== "USER" && (
                  <Hidden mdDown>
                    <TableCell key="delete" align="center"></TableCell>
                  </Hidden>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {props.isLoading ? (
                skeletonArray.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell component="th" scope="row" align="center">
                      <Skeleton />
                    </TableCell>
                    <TableCell component="th" scope="row" align="center">
                      <Skeleton />
                    </TableCell>
                    <Hidden mdDown>
                      <TableCell align="center">
                        {" "}
                        <Skeleton />
                      </TableCell>
                    </Hidden>
                    <Hidden mdDown>
                      <TableCell align="center">
                        {" "}
                        <Skeleton />
                      </TableCell>
                    </Hidden>
                    {localStorage.getItem("Role") !== "USER" && (
                      <Hidden mdDown>
                        <TableCell align="center">
                          <Skeleton />
                        </TableCell>
                      </Hidden>
                    )}
                  </TableRow>
                ))
              ) : props.versions.length > 0 ? (
                props.versions
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((element: Version) => (
                    <TableRow
                      key={element.id}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell
                        key={element.versionNumber}
                        component="th"
                        scope="row"
                        align="center"
                      >
                        {element.versionNumber}
                      </TableCell>
                      <Hidden mdDown>
                        <TableCell
                          component="th"
                          scope="row"
                          key={element.updated_at}
                          align="center"
                        >
                          {new Date(element.created_at).toLocaleString()}
                          {/*formatDistance(new Date(element.updated_at), new Date())*/}
                        </TableCell>
                        <TableCell key={element.changelog} align="center">
                          {element.changelog}
                        </TableCell>
                      </Hidden>
                      <TableCell
                        component="th"
                        scope="row"
                        key={"version-share-cell" + element.id}
                        align="center"
                      >
                        {props.platform === "Android" ? (
                          <LinkGenerationDesktop
                            name={props.name}
                            versions={props.versions}
                            platform={props.platform}
                            isVersionTable={true}
                            currentAndroidVersion={String(element.id)}
                            setCurrentAndroidVersion={() =>
                              setVersion(String(element.id))
                            }
                          />
                        ) : props.platform === "Android; iOS;" &&
                          element.platform === "IOS" &&
                          props.allVersions ? (
                          <LinkGenerationDesktop
                            name={props.name}
                            versions={props.allVersions}
                            platform={props.platform}
                            isVersionTable={true}
                            currentIosVersion={String(element.id)}
                            setCurrentIosVersion={() =>
                              setVersion(String(element.id))
                            }
                          />
                        ) : props.platform === "Android; iOS;" &&
                          element.platform === "ANDROID" &&
                          props.allVersions ? (
                          <LinkGenerationDesktop
                            name={props.name}
                            versions={props.allVersions}
                            platform={props.platform}
                            isVersionTable={true}
                            currentAndroidVersion={String(element.id)}
                            setCurrentAndroidVersion={() =>
                              setVersion(String(element.id))
                            }
                          />
                        ) : (
                          props.platform === "iOS" && (
                            <LinkGenerationDesktop
                              name={props.name}
                              versions={props.versions}
                              platform={props.platform}
                              isVersionTable={true}
                              currentIosVersion={String(element.id)}
                              setCurrentIosVersion={() =>
                                setVersion(String(element.id))
                              }
                            />
                          )
                        )}
                      </TableCell>
                      <Hidden mdDown>
                        {localStorage.getItem("Role") !== "USER" && (
                          <TableCell width="20%" align="center">
                            <Tooltip title={title[0]}>
                              <Button
                                onClick={() => handleSetDeleteInfo(element)}
                              >
                                <Delete color="action" />
                              </Button>
                            </Tooltip>
                          </TableCell>
                        )}
                      </Hidden>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4}>
                    <NoDataToBeShown />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          labelRowsPerPage={t("Rows per page")}
          rowsPerPageOptions={[5, 10, 25, 100]}
          component="div"
          count={props.versions.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
      {deletedVersion && (
        <DeleteElementFromTable
          open={openDelete}
          setOpen={setOpenDelete}
          entity="versions"
          id={deletedVersion.id}
          name={deletedVersion.versionNumber}
          appID={props.appID}
          versionArray={props.versions}
        />
      )}
    </Box>
  );
}

export default ApplicationsDetailsTable;
